import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AutotradeTrigger,
  CreateTriggerPayload,
  UpdateTriggerPayload,
} from '@crypto-trading/common/browser';
import {
  listTriggers,
  createTrigger,
  updateTrigger,
  deleteTrigger,
} from '@/api/triggers';

export const triggerKeys = {
  all: ['autotrade-triggers'] as const,
  byUser: (userId: string) => [...triggerKeys.all, userId] as const,
};

export const useUserTriggers = (userId: string | undefined) => {
  return useQuery<AutotradeTrigger[]>({
    queryKey: triggerKeys.byUser(userId || 'unknown'),
    queryFn: () => listTriggers(userId!),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
};

export const useCreateTrigger = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string;
      payload: CreateTriggerPayload;
    }) => createTrigger(userId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: triggerKeys.byUser(variables.userId),
      });
    },
  });
};

export const useUpdateTrigger = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      triggerId,
      userId,
      payload,
    }: {
      triggerId: string;
      userId: string;
      payload: UpdateTriggerPayload;
    }) => updateTrigger(triggerId, userId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: triggerKeys.byUser(variables.userId),
      });
    },
  });
};

export const useDeleteTrigger = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      triggerId,
      userId,
    }: {
      triggerId: string;
      userId: string;
    }) => deleteTrigger(triggerId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: triggerKeys.byUser(variables.userId),
      });
    },
  });
};
